import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from "react";

import LoadingSpinner from "modules/core/components/LoadingSpinner";

import { useUserLocalProjectsQuery } from "modules/projects/hooks/queries/useUserLocalProjectsQuery";
import { useSelectedProject } from "../../../projects/hooks/useSelectedProject";
import { useAuthUserQuery } from "../../hooks/queries/useAuthUserQuery";

import { IUser, UserRole } from "modules/user/types/entities";
import { IProject } from "modules/projects/types/entities";

import { ability } from "./ability";

export type AbilitySubject =
  | "experiment"
  | "idea"
  | "project"
  | "account"
  | "research"
  | "observation"
  | "todo_list"
  | "todo"
  | "crowdsource"
  | "user"
  | "saved_query"
  | "shared_experiments"
  | "shared_roadmap"
  | "comment"
  | "tag"
  | "people_stats"
  | "user_details"
  | "team"
  | "playbooks"
  | "objective"
  | "objective_target"
  | "objective_learning"
  | "group"
  | "collection"
  | "goal"
  | "project_notifications"
  | "insight"
  | "strategic_decision"
  | "brainstorming_idea";

export type UserAbility = {
  [subject in AbilitySubject]: {
    [action in PermissionAction]: (UserRole | "all")[];
  };
};

interface IGetAccessPermissionArgs {
  subject: AbilitySubject;
  action: PermissionAction;
  usingBestRole?: boolean;
  userData?: IUser;
  selectedProject?: IProject;
  projectId?: string;
  projectsData?: IProject[];
  ability: UserAbility;
  allowSuperAdmin?: boolean;
}

const getAccessPermission = (args: IGetAccessPermissionArgs): boolean => {
  const {
    subject,
    action,
    usingBestRole,
    userData,
    selectedProject,
    projectId,
    projectsData,
    ability,
    allowSuperAdmin
  } = args;

  if (allowSuperAdmin && userData?.super_admin) {
    return true;
  }

  const allowedRoles = ability[subject][action];

  if (allowedRoles.includes("all")) {
    return true;
  }

  if (usingBestRole) {
    const userBestRole = userData?.best_role;
    return allowedRoles.includes(userBestRole!);
  }

  if (projectId) {
    const project = (projectsData || []).find(proj => proj.id === projectId);

    const userRole = (project?.user_role || [])[0];

    return allowedRoles.includes(userRole);
  }

  const role = (selectedProject?.user_role || [])[0];

  return allowedRoles.includes(role);
};

export type PermissionAction =
  "create" | "read" | "update" | "delete" | "config";

export type CanAccess = (args: {
  I: PermissionAction;
  a: AbilitySubject;
  in?: string;
  usingBestRole?: boolean;
  allowSuperAdmin?: boolean;
}) => boolean;

interface IPermissionsContextValue {
  can: CanAccess;
}

const PermissionsContext = createContext<IPermissionsContextValue>(
  {} as IPermissionsContextValue
);

interface IProps {
  I: PermissionAction;
  a: AbilitySubject;
  in?: string;
  usingBestRole?: boolean;
  children: ReactNode | ((canAccess: boolean) => ReactNode);
  redirectURL?: string;
  allowSuperAdmin?: boolean;
  fallback?: ReactNode;
}

const Can = (props: IProps) => {
  const {
    I: action,
    a: subject,
    in: projectId,
    usingBestRole,
    allowSuperAdmin,
    children,
    redirectURL,
    fallback
  } = props;

  const { can } = useContext(PermissionsContext);

  const canAccess = useMemo(
    () =>
      can({
        I: action,
        a: subject,
        in: projectId,
        usingBestRole,
        allowSuperAdmin
      }),
    [can, action, subject, projectId, usingBestRole, allowSuperAdmin]
  );

  useEffect(() => {
    if (!canAccess && redirectURL) {
      window.location.href = redirectURL;
    }
  }, [canAccess, redirectURL]);

  if (typeof children === "function") {
    return <>{children(canAccess)}</>;
  }

  return canAccess ? <>{children}</> : fallback;
};

export const PermissionsProvider = ({ children }: PropsWithChildren) => {
  const { data: userData } = useAuthUserQuery();
  const { data: projectsData } = useUserLocalProjectsQuery({
    include_archived: true
  });
  const { selectedProject } = useSelectedProject();

  const can: CanAccess = useCallback(
    ({
      I: action,
      a: subject,
      in: projectId,
      usingBestRole,
      allowSuperAdmin
    }) =>
      getAccessPermission({
        subject,
        action,
        usingBestRole,
        userData,
        selectedProject: selectedProject as IProject,
        projectId,
        projectsData: projectsData || [],
        ability: ability,
        allowSuperAdmin
      }),
    [projectsData, selectedProject, userData]
  );

  const value = useMemo(() => ({ can }), [can]);

  if (!projectsData || !userData) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);

export default Can;
